import React, { useContext, useState, useEffect, useReducer, useRef, memo, useCallback } from "react";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import { isSameDay, isYesterday, isThisYear, parseISO, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green, red } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Badge
} from "@material-ui/core";

import {
  Clock as AccessTime,
  Ban as Block,
  Check as Done,
  CheckCheck as DoneAll,
  ChevronDown as ExpandMore,
  Download as GetApp,
  Facebook,
  Instagram,
  Reply,
  X as Close,
  Languages as Translate,
  SmilePlus,
  Trash2,
} from "lucide-react";

import MarkdownWrapper from "../MarkdownWrapper";
import ConfirmationModal from "../ConfirmationModal";
import EditMessageModal from "../EditMessageModal";
import ForwardModal from "../ForwardMessageModal";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import chatBgPattern from "../../assets/chat-bg-pattern.png";
import YouTubePreview from "../ModalYoutubeCors";

import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";

import { Popper, Paper, ClickAwayListener, useTheme, useMediaQuery, Dialog, Slide, List, ListItem, ListItemIcon, ListItemText, Typography } from "@material-ui/core";
import { Download as SaveAltIcon } from "lucide-react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AdMetaPreview from "../AdMetaPreview"; // Adicionado componente de preview de anúncio
import SaveFieldFromTextMenu from "../SaveFieldFromTextMenu";

import { messages } from "../../translate/languages";
import { useParams, useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  saveFieldFab: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: 20,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    zIndex: 9999,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  currentTick: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    width: "100%",
    maxWidth: "78%",
    margin: "14px auto",
    "&::before": {
      content: '""',
      flex: 1,
      height: "1px",
      marginRight: 14,
      background: theme.mode === "light"
        ? "linear-gradient(to left, rgba(17,27,33,0.18), transparent)"
        : "linear-gradient(to left, rgba(233,237,239,0.20), transparent)",
    },
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      marginLeft: 14,
      background: theme.mode === "light"
        ? "linear-gradient(to right, rgba(17,27,33,0.18), transparent)"
        : "linear-gradient(to right, rgba(233,237,239,0.20), transparent)",
    },
  },

  currentTicktText: {
    color: theme.mode === "light" ? "rgba(17,27,33,0.55)" : "rgba(233,237,239,0.55)",
    padding: "2px 0",
    alignSelf: "center",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  messagesList: {
    background: "transparent",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "85px 20px 20px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: "5px dashed #333",
    color: '#333',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeftWrapper: {
    display: "flex",
    alignItems: "center",
    alignSelf: "flex-start",
    marginTop: 2,
    marginBottom: 4,
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  messageWrapperWithReaction: {
    marginBottom: 22,
  },
  messageLeft: {
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "inline-block",
    position: "relative",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: theme.mode === 'light' ? "#e5e5e5" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
    },
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: 4,
    borderRadius: 4,
    margin: "8px 0 8px 8px",
    backgroundColor: "#388aff",
  },

  messageRightWrapper: {
    display: "flex",
    alignItems: "center",
    alignSelf: "flex-end",
    marginTop: 2,
    marginBottom: 4,
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  messageRightWrapperWithReaction: {
    marginBottom: 22,
  },
  messageRight: {
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "inline-block",
    position: "relative",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: theme.mode === 'light' ? "#e9edf2" : "#192535",
    color: theme.mode === 'light' ? "#1a1a1a" : "#e9edf2",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
    },
  },

  messageRightPrivate: {
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "inline-block",
    position: "relative",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: theme.mode === "light" ? "#ede9fe" : "#2d2440",
    border: theme.mode === "light" ? "1px solid #a78bfa" : "1px solid #4c3d6e",
    color: theme.mode === "light" ? "#4c1d95" : "#e9d5ff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000",
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
    },
  },
  reactionBadge: {
    position: "absolute",
    bottom: -19,
    minWidth: 28,
    height: 21,
    padding: "0 7px",
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    fontSize: 15,
    lineHeight: "21px",
    zIndex: 4,
    cursor: "pointer",
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
    boxShadow: theme.mode === "light" ? "0 1px 2px rgba(0,0,0,0.22)" : "0 1px 3px rgba(0,0,0,0.55)",
    border: theme.mode === "light" ? "1px solid rgba(0,0,0,0.04)" : "1px solid rgba(255,255,255,0.08)",
  },
  reactionBadgeRight: {
    right: 8,
  },
  reactionBadgeLeft: {
    left: 8,
  },
  reactionCount: {
    fontSize: 11,
    color: theme.mode === "light" ? "#667781" : "#aebac1",
    marginLeft: 1,
  },
  reactionsDialogPaper: {
    borderRadius: "16px !important",
    width: 420,
    maxWidth: "calc(100vw - 32px)",
    minHeight: 240,
    overflow: "hidden",
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#111b21",
    color: theme.mode === "light" ? "#111b21" : "#e9edef",
    boxShadow: theme.mode === "light" ? "0 18px 48px rgba(11,20,26,0.20)" : "0 22px 52px rgba(0,0,0,0.55)",
  },
  reactionsDialogContent: {
    padding: 0,
  },
  reactionsHeader: {
    padding: "18px 20px 12px",
    borderBottom: theme.mode === "light" ? "1px solid #eef0f1" : "1px solid rgba(255,255,255,0.08)",
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#111b21",
  },
  reactionsTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.mode === "light" ? "#111b21" : "#e9edef",
    marginBottom: 14,
  },
  reactionsTabs: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  reactionsAllTab: {
    width: 36,
    height: 36,
    padding: 0,
    border: 0,
    borderRadius: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.mode === "light" ? "#54656f" : "#aebac1",
    backgroundColor: theme.mode === "light" ? "#f0f2f5" : "#202c33",
    cursor: "pointer",
    transition: "all 0.18s ease",
    "&:hover": {
      color: theme.mode === "light" ? "#111b21" : "#e9edef",
      backgroundColor: theme.mode === "light" ? "#e6e8eb" : "#2a3942",
    },
  },
  reactionsEmojiTab: {
    minWidth: 58,
    height: 36,
    padding: "0 10px",
    borderRadius: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    backgroundColor: theme.mode === "light" ? "#d9fdd3" : "#1f3f35",
    border: theme.mode === "light" ? "1px solid #b7e6b2" : "1px solid rgba(134,198,121,0.35)",
    fontSize: 18,
  },
  reactionsList: {
    padding: "8px 20px 16px",
  },
  reactionDialogRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 0",
    borderRadius: 10,
    cursor: "default",
  },
  reactionDialogRowSelf: {
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#f7f8f8" : "rgba(255,255,255,0.04)",
    },
  },
  reactionAvatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    background: theme.mode === "light"
      ? "linear-gradient(135deg, #dfe5e7, #f0f2f5)"
      : "linear-gradient(135deg, #263238, #1f2c34)",
    color: theme.mode === "light" ? "#54656f" : "#d1d7db",
    fontWeight: 600,
  },
  reactionUserInfo: {
    flex: 1,
    minWidth: 0,
  },
  reactionUserName: {
    fontSize: 15,
    color: theme.mode === "light" ? "#111b21" : "#e9edef",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  reactionHint: {
    fontSize: 12,
    color: theme.mode === "light" ? "#8696a0" : "#8696a0",
    marginTop: 3,
  },
  reactionDialogEmoji: {
    width: 38,
    height: 38,
    borderRadius: 19,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.mode === "light" ? "#f0f2f5" : "#202c33",
    fontSize: 24,
    flexShrink: 0,
  },
  reactionRemoveButton: {
    border: 0,
    width: 32,
    height: 32,
    padding: 6,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    color: theme.mode === "light" ? "#c62828" : "#ff8a80",
    backgroundColor: "transparent",
    transition: "all 0.18s ease",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "rgba(229,57,53,0.10)" : "rgba(255,138,128,0.14)",
      color: theme.mode === "light" ? "#c62828" : "#ff8a80",
    },
  },
  reactionEmojiPicker: {
    "& .emoji-mart": {
      borderRadius: 12,
      border: theme.mode === "light" ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(255,255,255,0.1)",
      boxShadow: theme.mode === "light"
        ? "0 4px 20px rgba(0,0,0,0.12)"
        : "0 4px 20px rgba(0,0,0,0.4)",
      backgroundColor: theme.mode === "light" ? "#ffffff" : "#1f2c34",
    },
    "& .emoji-mart-bar": {
      borderColor: theme.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
    },
    "& .emoji-mart-search input": {
      borderRadius: 8,
      backgroundColor: theme.mode === "light" ? "#f0f2f5" : "#2a3942",
      borderColor: theme.mode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
      color: theme.mode === "light" ? "#111b21" : "#e9edef",
    },
    "& .emoji-mart-category-label span": {
      backgroundColor: theme.mode === "light" ? "#ffffff" : "#1f2c34",
      color: theme.mode === "light" ? "#667781" : "#8696a0",
    },
    "& .emoji-mart-scroll": {
      ...theme.scrollbarStyles,
    },
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#d6dde5" : "#111c2a",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: 4,
    borderRadius: 4,
    margin: "8px 0 8px 8px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    position: "absolute",
    top: 2,
    right: 4,
    width: 24,
    height: 24,
    minWidth: 24,
    padding: 0,
    color: theme.mode === "light" ? "#54656f" : "#aebac1",
    zIndex: 3,
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none",
    transition: "opacity 0.12s ease, background-color 0.12s ease",
    backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.68)" : "rgba(17,27,33,0.72)",
    boxShadow: theme.mode === "light" ? "-10px 0 12px rgba(255,255,255,0.65)" : "-10px 0 12px rgba(17,27,33,0.45)",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.92)" : "rgba(42,57,66,0.95)",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  messageActionsButtonOpen: {
    opacity: 1,
    visibility: "visible",
    pointerEvents: "auto",
  },

  // Barra de ações inline — mensagens enviadas (fromMe): ao lado esquerdo da bolha
  inlineActionsBar: {
    display: "none",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingRight: 8,
    flexShrink: 0,
    order: -1,
  },
  // Barra de ações inline — mensagens recebidas (!fromMe): ao lado direito da bolha
  inlineActionsBarLeft: {
    display: "none",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingLeft: 8,
    flexShrink: 0,
  },
  inlineActionIcon: {
    padding: 0,
    color: theme.mode === "light" ? "#54656f" : "#8696a0",
    cursor: "pointer",
    transition: "color 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: theme.mode === "light" ? "#111b21" : "#e9edef",
    },
  },
  inlineActionIconDanger: {
    padding: 0,
    color: theme.mode === "light" ? "#54656f" : "#8696a0",
    cursor: "pointer",
    transition: "color 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: "#e53935",
    },
  },
  inlineActionIconStarred: {
    padding: 0,
    color: "#f9a825",
    cursor: "pointer",
    transition: "color 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: "#f57f17",
    },
  },

  // Bottom sheet mobile
  bottomSheet: {
    borderRadius: "16px 16px 0 0",
    maxHeight: "60vh",
    padding: "8px 0 16px",
  },
  bottomSheetHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
    margin: "8px auto 12px",
  },
  bottomSheetItem: {
    padding: "12px 20px",
    "&:hover": {
      background: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    },
  },
  bottomSheetIcon: {
    minWidth: 40,
    color: theme.mode === "dark" ? "#8696a0" : "#54656f",
  },
  bottomSheetDanger: {
    color: "#e53935",
  },

  messageContactName: {
    display: "flex",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: "100%",
    height: "auto",
    borderRadius: 12,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },

  translatedIndicator: {
    fontSize: 11,
    color: "#667781",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    padding: "3px 0 1px",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    userSelect: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },

  translatedIcon: {
    fontSize: 14,
  },

  transcribeBtn: {
    fontSize: 11,
    color: "#667781",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
    padding: "3px 0 1px",
    userSelect: "none",
    "&:hover": {
      textDecoration: "underline",
    }
  },

  transcriptionText: {
    fontSize: 12,
    color: "#303030",
    marginTop: 4,
    padding: "6px 8px",
    backgroundColor: "rgba(0,0,0,0.04)",
    borderRadius: 6,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  transcriptionLang: {
    fontSize: 10,
    color: "#667781",
    marginTop: 2,
    fontStyle: "italic",
  },

  dailyTimestamp: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    width: "100%",
    maxWidth: "78%",
    margin: "14px auto",
    "&::before": {
      content: '""',
      flex: 1,
      height: "1px",
      marginRight: 14,
      background: theme.mode === "light"
        ? "linear-gradient(to left, rgba(17,27,33,0.18), transparent)"
        : "linear-gradient(to left, rgba(233,237,239,0.20), transparent)",
    },
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      marginLeft: 14,
      background: theme.mode === "light"
        ? "linear-gradient(to right, rgba(17,27,33,0.18), transparent)"
        : "linear-gradient(to right, rgba(233,237,239,0.20), transparent)",
    },
  },

  dailyTimestampText: {
    color: theme.mode === "light" ? "rgba(17,27,33,0.55)" : "rgba(233,237,239,0.55)",
    padding: "2px 0",
    alignSelf: "center",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },
  audioMessagePlayer: {
    display: "inline-block",
    width: 320,
    maxWidth: "100%",
    overflow: "hidden",
    padding: "5px 2px 3px",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(100vw - 88px)",
    },
  },
  audioMessageNative: {
    width: "100%",
    height: 36,
    display: "block",
    borderRadius: 18,
    overflow: "hidden",
    backgroundColor: theme.mode === "light" ? "#f1f5f9" : "#31414b",
    colorScheme: theme.mode === "light" ? "light" : "dark",
    "&::-webkit-media-controls-panel": {
      backgroundColor: theme.mode === "light" ? "#f1f5f9" : "#31414b",
    },
  },
  docCardChat: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    margin: 4,
    borderRadius: 10,
    background: theme.mode === "light" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      background: theme.mode === "light" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.1)",
    },
  },
  docCardChatIcon: {
    width: 36,
    height: 36,
    objectFit: "contain",
    flexShrink: 0,
  },
  docCardChatInfo: {
    flex: 1,
    minWidth: 0,
  },
  docCardChatName: {
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  docCardChatMeta: {
    fontSize: 11,
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    marginTop: 1,
  },
  docCardChatDownload: {
    flexShrink: 0,
    padding: 4,
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      color: theme.mode === "light" ? "#111b21" : "#e9edef",
    },
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload;
    return state.filter((m) => m.id !== messageId);
  }

  if (action.type === "REMOVE_OWN_REACTION") {
    const messageId = action.payload;
    return state.filter((m) => {
      if (m?.mediaType !== "reactionMessage" || !m.fromMe) return true;
      const quotedId = m?.quotedMsg?.id || m?.quotedMsgId;
      return String(quotedId) !== String(messageId);
    });
  }

  if (action.type === "RESET") {
    return [];
  }
};

const groupContactColors = [
  "#e15d44", "#7cb342", "#039be5", "#8e24aa",
  "#d81b60", "#00897b", "#f4511e", "#3949ab",
  "#c0ca33", "#6d4c41", "#00acc1", "#e53935",
  "#5e35b1", "#43a047", "#1e88e5", "#fb8c00",
];

const getContactColor = (name) => {
  if (!name) return groupContactColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return groupContactColors[Math.abs(hash) % groupContactColors.length];
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  contactId,
  bottomPadding,
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  // Inline actions state
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteTargetMsg, setDeleteTargetMsg] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTargetMsg, setEditTargetMsg] = useState(null);
  const [reactAnchorEl, setReactAnchorEl] = useState(null);
  const [reactTargetMsg, setReactTargetMsg] = useState(null);

  const [selectionText, setSelectionText] = useState("");
  const [selectionAnchorEl, setSelectionAnchorEl] = useState(null);
  const [saveFieldOpen, setSaveFieldOpen] = useState(false);
  const selectionVirtualRef = useRef(null);

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);
  const [showOriginal, setShowOriginal] = useState({});
  const [transcriptions, setTranscriptions] = useState({});
  const [transcribing, setTranscribing] = useState({});
  const [localReactions, setLocalReactions] = useState({});
  const [reactionDialog, setReactionDialog] = useState({ open: false, reactions: [] });
  const [reactionDialogEmojiAnchor, setReactionDialogEmojiAnchor] = useState(null);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  const { showSelectMessageCheckbox, setShowSelectMessageCheckbox, setSelectedMessages } = useContext(ForwardMessageContext);

  const { user, socket } = useContext(AuthContext);

  const isCurrentTicketEvent = useCallback((data) => {
    const eventTicket = data?.ticket || data?.message?.ticket;
    const candidates = [
      eventTicket?.uuid,
      eventTicket?.id,
      data?.ticketId,
      data?.message?.ticketId,
    ].filter((value) => value !== undefined && value !== null);

    return candidates.some((value) => String(value) === String(ticketId));
  }, [ticketId]);

  // Mobile long-press bottom sheet
  const mobileTheme = useTheme();
  const isMobile = useMediaQuery(mobileTheme.breakpoints.down("sm"));
  const [longPressMsg, setLongPressMsg] = useState(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const longPressTimer = useRef(null);
  const longPressMoved = useRef(false);

  const handleTouchStart = useCallback((message) => {
    if (!isMobile) return;
    longPressMoved.current = false;
    longPressTimer.current = setTimeout(() => {
      if (!longPressMoved.current) {
        setLongPressMsg(message);
        setBottomSheetOpen(true);
        if (navigator.vibrate) navigator.vibrate(30);
      }
    }, 500);
  }, [isMobile]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  const handleTouchMove = useCallback(() => {
    longPressMoved.current = true;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  const closeBottomSheet = useCallback(() => {
    setBottomSheetOpen(false);
    setLongPressMsg(null);
  }, []);

  const focusMessageInput = useCallback(() => {
    setTimeout(() => {
      const el = document.querySelector("textarea[class*='messageInput']")
        || document.querySelector("input[class*='messageInput']")
        || document.querySelector("[class*='messageInput'] input")
        || document.querySelector("[class*='messageInput'] textarea");
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
  }, []);

  const companyId = user.companyId;

  useEffect(() => {

    async function fetchData() {

      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {

        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) {
          return;
        }
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);

            // Carregar transcrições já salvas no banco
            const savedTranscriptions = {};
            data.messages.forEach((msg) => {
              if (msg.transcription) {
                try {
                  const parsed = JSON.parse(msg.transcription);
                  savedTranscriptions[msg.id] = {
                    transcribedText: parsed.transcribedText || null,
                    translatedText: parsed.translatedText || null,
                    detectedLanguage: parsed.detectedLanguage || null,
                    showOriginal: false,
                  };
                } catch (e) {}
              }
            });
            if (Object.keys(savedTranscriptions).length > 0) {
              setTranscriptions(prev => ({ ...prev, ...savedTranscriptions }));
            }
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          console.error("❌ [MessagesList] Erro ao buscar mensagens:", err);
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }
    if (!socket || typeof socket.on !== "function") {
      return;
    }

    const companyId = user.companyId;
    if (!companyId) {
      return;
    }

    // ✅ CORREÇÃO: Usar ticketId (UUID) diretamente
    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", ticketId);
    };

    // Join immediately se já estiver conectado — não esperar próximo "connect"
    socket.emit("joinChatBox", ticketId);

    const onAppMessageMessagesList = (data) => {
      if (data.action === "create" && isCurrentTicketEvent(data)) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update" && isCurrentTicketEvent(data)) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action == "delete" && isCurrentTicketEvent(data)) {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    };
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      if (socket && typeof socket.off === "function") {
        socket.emit("joinChatBoxLeave", ticketId);
        socket.off("connect", connectEventMessagesList);
        socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
      }
    };

  }, [ticketId, socket, user.companyId, isCurrentTicketEvent]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

const scrollToBottom = () => {
  setTimeout(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  }, 100);
};

const handleScroll = (e) => {
  if (!hasMore) return;
  const { scrollTop } = e.currentTarget;

  if (scrollTop === 0) {
    document.getElementById("messagesList").scrollTop = 1;
  }

  if (loading) {
    return;
  }

  if (scrollTop < 50) {
    loadMore();
  }
};

const handleTextSelection = useCallback(() => {
  const selection = window.getSelection();
  const text = selection ? selection.toString().trim() : "";

  if (text.length >= 3 && contactId) {
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    selectionVirtualRef.current = {
      getBoundingClientRect: () => rect,
      clientWidth: rect.width,
      clientHeight: rect.height,
    };
    setSelectionText(text);
    setSelectionAnchorEl(selectionVirtualRef.current);
  } else {
    setSelectionAnchorEl(null);
    setSelectionText("");
  }
}, [contactId]);

const handleOpenMessageOptionsMenu = (e, message) => {
  e.stopPropagation();
  setAnchorEl(e.currentTarget);
  setSelectedMessage(message);
};

const handleCloseMessageOptionsMenu = (e) => {
  setAnchorEl(null);
};

const hanldeReplyMessage = (e, message) => {
  setAnchorEl(null);
  setReplyingMessage(message);
};

const renderMessageActionsButton = (message) => {
  if (isMobile || message.isDeleted) return null;

  return (
    <IconButton
      variant="contained"
      size="small"
      className={clsx(classes.messageActionsButton, {
        [classes.messageActionsButtonOpen]: selectedMessage?.id === message.id && messageOptionsMenuOpen,
      })}
      onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
    >
      <ExpandMore size={18} />
    </IconButton>
  );
};

// Inline action handlers
const handleInlineDelete = (message, e) => {
  setDeleteTargetMsg(message);
  setDeleteAnchorEl(e?.currentTarget || null);
};

const handleConfirmDelete = async () => {
  if (!deleteTargetMsg) return;
  try {
    await api.delete(`/messages/${deleteTargetMsg.id}`);
  } catch (err) {
    toastError(err);
  }
  setDeleteAnchorEl(null);
  setDeleteTargetMsg(null);
};

const handleCancelDelete = () => {
  setDeleteAnchorEl(null);
  setDeleteTargetMsg(null);
};

const handleInlineEdit = (message) => {
  setEditTargetMsg(message);
  setEditModalOpen(true);
};

const handleMessageUpdated = useCallback((updatedMessage) => {
  if (!updatedMessage?.id) return;
  dispatch({ type: "UPDATE_MESSAGE", payload: updatedMessage });
  setSelectedMessage((prev) => (prev?.id === updatedMessage.id ? updatedMessage : prev));
  setEditTargetMsg((prev) => (prev?.id === updatedMessage.id ? updatedMessage : prev));
}, []);

const handleInlineForward = (message) => {
  setSelectedMessages([message]);
  setShowSelectMessageCheckbox(true);
};

const handleInlineStar = async (message) => {
  try {
    await api.put(`/messages/${message.id}/star`);
  } catch (err) {
    toastError(err);
  }
};

const handleInlineReact = (e, message) => {
  setReactAnchorEl(e.currentTarget);
  setReactTargetMsg(message);
};

const handleReactToMessage = async (reactionType) => {
  if (!reactTargetMsg) return;
  try {
    await api.post(`/messages/${reactTargetMsg.id}/reactions`, { type: reactionType });
    handleReactionAdded(reactTargetMsg.id, reactionType);
  } catch (err) {
    toastError(err);
  }
  setReactAnchorEl(null);
  setReactTargetMsg(null);
};

const availableReactionsInline = ['😀', '😂', '❤️', '👍', '🎉', '😢', '😮', '😡', '👏', '🔥'];
const checkMessageMedia = (message) => {
  if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
    let locationParts = message.body.split('|')
    let imageLocation = locationParts[0]
    let linkLocation = locationParts[1]

    let descriptionLocation = null

    if (locationParts.length > 2)
      descriptionLocation = message.body.split('|')[2]

    return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
  } else

    if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} queueId={message?.ticket?.queueId} whatsappId={message?.ticket?.whatsappId} />
    } else if (message.mediaType === "adMetaPreview") { // Adicionado para renderizar o componente de preview de anúncio
  let [image, sourceUrl, title, body] = message.body.split('|');
  let messageUser = i18n.t("messagesResponsive.ads.defaultUserMessage");
  return <AdMetaPreview image={image} sourceUrl={sourceUrl} title={title} body={body} messageUser={messageUser} />;
}

      if (message.mediaType === "image") {
        return <ModalImageCors imageUrl={message.mediaUrl} />;
      } else

        if (message.mediaType === "audio") {
          return (
            <div className={classes.audioMessagePlayer}>
              <audio
                className={classes.audioMessageNative}
                src={message.mediaUrl}
                controls
                preload="metadata"
              />
              {renderAudioTranscription(message)}
            </div>
          );
        } else

          if (message.mediaType === "video") {
            return (
              <video
                className={classes.messageMedia}
                src={message.mediaUrl}
                controls
              />
            );
          } else {
            const url = message.mediaUrl || "";
            const msgBody = message.body || "";
            const parts = (url || msgBody).split(".");
            const extRaw = parts.length > 1 ? parts.pop().split(/[?#]/)[0] : "";
            const ext = (extRaw && extRaw.length <= 5 && /^[a-zA-Z0-9]+$/.test(extRaw)) ? extRaw.toLowerCase() : "";
            const getDocIcon = (e) => {
              const l = (e || "").toLowerCase();
              if (l === "pdf") return "/imagens/PDF.png";
              if (["doc", "docx"].includes(l)) return "/imagens/word.png";
              if (["xls", "xlsx", "csv"].includes(l)) return "/imagens/excel.png";
              if (l === "txt") return "/imagens/txt.png";
              return "/imagens/doc.png";
            };
            const fileName = msgBody || url.split("/").pop() || "Documento";
            const isPdf = ext === "pdf";
            const handleDocDownload = async () => {
              try {
                const res = await fetch(url);
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
              } catch { window.open(url, "_blank"); }
            };
            return (
              <div style={{ margin: 4 }}>
                {/* PDF preview fallback */}
                {isPdf && (
                  <div style={{
                    borderRadius: "10px 10px 0 0",
                    overflow: "hidden",
                    background: "#f5f5f5",
                    maxHeight: 260,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <iframe
                      src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                      title="PDF preview"
                      style={{ width: "100%", height: 260, border: "none", pointerEvents: "none" }}
                    />
                  </div>
                )}
                {/* File info row */}
                <div className={classes.docCardChat} style={{ borderRadius: isPdf ? "0 0 10px 10px" : 10, margin: 0, flexDirection: "column", padding: 0, cursor: "default" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", width: "100%" }}>
                    <img src={getDocIcon(ext)} alt={ext.toUpperCase()} className={classes.docCardChatIcon} />
                    <div className={classes.docCardChatInfo}>
                      <div className={classes.docCardChatName}>{fileName}</div>
                      <div className={classes.docCardChatMeta}>{ext ? ext.toUpperCase() : "DOC"}</div>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div style={{
                    display: "flex",
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                    width: "100%",
                  }}>
                    {isPdf && (
                      <button
                        onClick={() => window.open(url, "_blank")}
                        style={{ flex: 1, padding: "8px 0", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#1a73e8", borderRight: "1px solid rgba(0,0,0,0.06)" }}
                      >
                        Visualizar
                      </button>
                    )}
                    <button
                      onClick={() => window.open(url, "_blank")}
                      style={{ flex: 1, padding: "8px 0", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#1a73e8", borderRight: "1px solid rgba(0,0,0,0.06)" }}
                    >
                      Abrir
                    </button>
                    <button
                      onClick={handleDocDownload}
                      style={{ flex: 1, padding: "8px 0", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#1a73e8" }}
                    >
                      Baixar
                    </button>
                  </div>
                </div>
              </div>
            );
          }
};

const renderMessageAck = (message) => {
  if (message.ack === 0) {
    return <AccessTime size={16} className={classes.ackIcons} />;
  } else
    if (message.ack === 1) {
      return <Done size={16} className={classes.ackIcons} />;
    } else
      if (message.ack === 2) {
        return <DoneAll size={16} className={classes.ackIcons} />;
      } else
        if (message.ack === 3 || message.ack === 4) {
          return <DoneAll size={16} className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
        } else
          if (message.ack === 5) {
            return <DoneAll size={16} className={classes.ackDoneAllIcon} />
          }
};

const formatDailyLabel = (date) => {
  if (isSameDay(date, new Date())) return i18n.t("messagesResponsive.timestamp.today");
  if (isYesterday(date)) {
    return i18n.t("messagesResponsive.timestamp.yesterday", { defaultValue: "ONTEM" });
  }
  if (isThisYear(date)) return format(date, "d 'de' MMMM", { locale: ptBR });
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
};

const renderDailyTimestamps = (message, index) => {
  const messageDay = parseISO(messagesList[index].createdAt);

  const divider = (
    <div className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
      <span className={classes.dailyTimestampText}>{formatDailyLabel(messageDay)}</span>
    </div>
  );

  if (index === 0) return divider;

  if (index < messagesList.length - 1) {
    const previousMessageDay = parseISO(messagesList[index - 1].createdAt);
    if (!isSameDay(messageDay, previousMessageDay)) return divider;
    return null;
  }

  if (index === messagesList.length - 1) {
    const previousMessageDay =
      messagesList[index - 1] && parseISO(messagesList[index - 1].createdAt);
    const dayChanged = previousMessageDay && !isSameDay(messageDay, previousMessageDay);
    return (
      <>
        {dayChanged && divider}
        <div
          key={`ref-${message.id}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      </>
    );
  }

  return null;
};


const renderTicketsSeparator = (message, index) => {
  let lastTicket = messagesList[index - 1]?.ticketId;
  let currentTicket = message.ticketId;

  if (lastTicket !== currentTicket && lastTicket !== undefined) {
    if (message?.ticket?.queue) {
      return (
        <div
          className={classes.currentTick}
          key={`timestamp-${message.id}a`}
        >
          <span className={classes.currentTicktText}>
            #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
          </span>
        </div>
      );
    } else {
      return (
        <div
          className={classes.currentTick}
          key={`timestamp-${message.id}b`}
        >
          <span className={classes.currentTicktText}>
            #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
          </span>
        </div>
      );
    }
  }

};

const renderMessageDivider = (message, index) => {
  if (index < messagesList.length && index > 0) {
    let messageUser = messagesList[index].fromMe;
    let previousMessageUser = messagesList[index - 1].fromMe;
    if (messageUser !== previousMessageUser) {
      return (

        <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
      );
    }
  }
};

const getBasename = (p) => { if (!p) return ""; const i = p.lastIndexOf("/"); return i >= 0 ? p.substring(i + 1) : p; };

const canEditMessage = (message) => {
  if (!message?.fromMe || message?.isDeleted) return false;

  const mediaType = message.mediaType || "";
  const body = (message.body || "").trim();
  if (!body) return false;

  const blockedTypes = [
    "audio",
    "document",
    "locationMessage",
    "contactMessage",
    "reactionMessage",
    "template",
    "adMetaPreview",
    "call_log",
  ];

  if (["image", "video"].includes(mediaType)) {
    const mediaName = getBasename(message.mediaUrl).split(/[?#]/)[0].trim();
    return body !== mediaName;
  }

  return !blockedTypes.includes(mediaType);
};

const handleReactionAdded = useCallback((messageId, reactionType) => {
  if (!messageId || !reactionType) return;
  setLocalReactions((prev) => ({
    ...prev,
    [messageId]: {
      key: "me",
      messageId,
      reaction: reactionType,
      name: i18n.t("messagesInput.you") || "Você",
      isSelf: true,
    },
  }));
}, []);

// Helper para obter o corpo da mensagem (com suporte a tradução)
const getMessageBody = (message) => {
  // Mensagens recebidas (fromMe=false): translatedBody = tradução para idioma do atendente
  // Mensagens enviadas (fromMe=true): body = texto original do atendente, translatedBody = o que foi enviado traduzido
  if (message.translatedBody && !message.fromMe) {
    // Mensagem recebida traduzida: mostra tradução por padrão
    return showOriginal[message.id] ? message.body : message.translatedBody;
  }
  return message.body;
};

const toggleOriginal = (messageId) => {
  setShowOriginal(prev => ({ ...prev, [messageId]: !prev[messageId] }));
};

const handleTranscribeAudio = async (message) => {
  if (transcribing[message.id]) return;

  // Extract filename from mediaUrl
  const url = message.mediaUrl || "";
  const fileName = url.split("/").pop();
  if (!fileName) return;

  setTranscribing(prev => ({ ...prev, [message.id]: true }));
  try {
    const { data } = await api.get(`/messages/transcribeAudio/${fileName}`);
    setTranscriptions(prev => ({
      ...prev,
      [message.id]: {
        transcribedText: data.transcribedText,
        translatedText: data.translatedText,
        detectedLanguage: data.detectedLanguage,
        showOriginal: false,
      }
    }));
  } catch (err) {
    toastError(err);
  }
  setTranscribing(prev => ({ ...prev, [message.id]: false }));
};

const renderAudioTranscription = (message) => {
  const t = transcriptions[message.id];
  if (!t) {
    return (
      <span
        className={classes.transcribeBtn}
        onClick={() => handleTranscribeAudio(message)}
      >
        {transcribing[message.id] ? (
          <>
            <CircularProgress size={12} />
            {i18n.t("messagesResponsive.transcription.transcribing")}
          </>
        ) : (
          <>
            <Translate className={classes.translatedIcon} />
            {i18n.t("messagesResponsive.transcription.transcribe")}
          </>
        )}
      </span>
    );
  }

  // Mostra o texto traduzido (se disponível) ou o transcrito original
  const displayText = t.translatedText || t.transcribedText;

  return (
    <div>
      <div className={classes.transcriptionText}>
        {displayText}
      </div>
    </div>
  );
};

const getMessageReactionDetails = useCallback((message) => {
  if (!message?.id) return [];

  const reactionByAuthor = new Map();
  messagesList.forEach((candidate) => {
    if (candidate?.mediaType !== "reactionMessage") return;
    const quotedId = candidate?.quotedMsg?.id || candidate?.quotedMsgId;
    const reactionBody = (candidate.body || "").trim();
    if (String(quotedId) === String(message.id) && reactionBody && reactionBody !== "reaction") {
      const key = candidate.fromMe
        ? "me"
        : `contact-${candidate.contact?.id || candidate.contact?.name || candidate.id}`;
      reactionByAuthor.set(key, {
        key,
        messageId: message.id,
        reaction: reactionBody,
        name: candidate.fromMe
          ? (i18n.t("messagesInput.you") || "Você")
          : (candidate.contact?.name || "Contato"),
        isSelf: Boolean(candidate.fromMe),
      });
    }
  });

  if (localReactions[message.id]) {
    reactionByAuthor.set("me", localReactions[message.id]);
  }

  return Array.from(reactionByAuthor.values()).filter((item) => item.reaction);
}, [messagesList, localReactions]);

const handleRemoveOwnReaction = async (reactionItem) => {
  if (!reactionItem?.isSelf || !reactionItem?.messageId) return;

  try {
    await api.post(`/messages/${reactionItem.messageId}/reactions`, { type: "" });
    setLocalReactions((prev) => {
      const next = { ...prev };
      delete next[reactionItem.messageId];
      return next;
    });
    dispatch({ type: "REMOVE_OWN_REACTION", payload: reactionItem.messageId });
    setReactionDialog((prev) => {
      const reactions = prev.reactions.filter((item) => item.key !== reactionItem.key);
      return reactions.length ? { ...prev, reactions } : { open: false, reactions: [] };
    });
  } catch (err) {
    toastError(err);
  }
};

const handleDialogReactionSelect = async (reactionType) => {
  const targetMessageId = reactionDialog.reactions?.[0]?.messageId;
  if (!targetMessageId || !reactionType) return;

  try {
    await api.post(`/messages/${targetMessageId}/reactions`, { type: reactionType });
    handleReactionAdded(targetMessageId, reactionType);
    setReactionDialog((prev) => {
      const existing = prev.reactions.filter((item) => item.key !== "me");
      return {
        ...prev,
        reactions: [
          {
            key: "me",
            messageId: targetMessageId,
            reaction: reactionType,
            name: i18n.t("messagesInput.you") || "Você",
            isSelf: true,
          },
          ...existing,
        ],
      };
    });
    setReactionDialogEmojiAnchor(null);
  } catch (err) {
    toastError(err);
  }
};

const renderMessageReactions = (message, fromMe) => {
  const reactions = getMessageReactionDetails(message);
  if (!reactions.length) return null;
  const visibleReactions = [...new Set(reactions.map((item) => item.reaction))].slice(0, 3);

  return (
    <span
      className={clsx(classes.reactionBadge, {
        [classes.reactionBadgeRight]: fromMe,
        [classes.reactionBadgeLeft]: !fromMe,
      })}
      onClick={(event) => {
        event.stopPropagation();
        setReactionDialog({ open: true, reactions });
      }}
    >
      {visibleReactions.map((reaction) => (
        <span key={reaction}>{reaction}</span>
      ))}
      {reactions.length > 1 && (
        <span className={classes.reactionCount}>{reactions.length}</span>
      )}
    </span>
  );
};

const renderTranslationIndicator = (message) => {
  if (!message.translatedBody) return null;

  if (!message.fromMe) {
    // Mensagem recebida traduzida
    const isShowingOriginal = showOriginal[message.id];
    const langLabel = message.originalLanguage || "?";
    return (
      <span
        className={classes.translatedIndicator}
        onClick={() => toggleOriginal(message.id)}
      >
        <Translate className={classes.translatedIcon} />
        {isShowingOriginal
          ? i18n.t("messagesResponsive.translation.showTranslation") || "Ver tradução"
          : (i18n.t("messagesResponsive.translation.translatedFrom") || "Traduzido de") + " " + langLabel}
      </span>
    );
  } else {
    // Mensagem enviada traduzida: mostrar o que foi enviado ao contato
    const isShowingTranslated = showOriginal[message.id];
    return (
      <span
        className={classes.translatedIndicator}
        onClick={() => toggleOriginal(message.id)}
      >
        <Translate className={classes.translatedIcon} />
        {isShowingTranslated
          ? i18n.t("messagesResponsive.translation.showOriginal") || "Ver original"
          : i18n.t("messagesResponsive.translation.autoTranslated") || "Tradução automática enviada"}
      </span>
    );
  }
};

const renderQuotedMessage = (message) => {

  return (
    <div
      className={clsx(classes.quotedContainerLeft, {
        [classes.quotedContainerRight]: message.fromMe,
      })}
    >
      <span
        className={clsx(classes.quotedSideColorLeft, {
          [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
        })}
      ></span>
      <div className={classes.quotedMsg}>
        <span className={classes.messageContactName}>
          {message.quotedMsg?.fromMe ? i18n.t("messagesInput.you") : message.quotedMsg?.contact?.name}
        </span>

        {message.quotedMsg.mediaType === "audio"
          && (
            <div className={classes.downloadMedia}>
              <div className={classes.audioMessagePlayer}>
                <audio
                  className={classes.audioMessageNative}
                  src={message.quotedMsg.mediaUrl}
                  controls
                  preload="metadata"
                />
              </div>

              {/* <audio controls>
                  <source src={message.quotedMsg.mediaUrl} type="audio/mp3"></source>
                  {/* <source src={message.quotedMsg.mediaUrl} type="audio/ogg"></source> 
                </audio> */}
            </div>
          )
        }
        {message.quotedMsg.mediaType === "video"
          && (
            <video
              className={classes.messageMedia}
              src={message.quotedMsg.mediaUrl}
              controls
            />
          )
        }
        {message.quotedMsg.mediaType === "contactMessage"
          && (
            i18n.t("messagesResponsive.types.contact")
          )
        }
        {message.quotedMsg.mediaType === "application"
          && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={<GetApp />}
                // color="primary"
                variant="outlined"
                target="_blank"
                href={message.quotedMsg.mediaUrl}
              >
                {i18n.t("messagesResponsive.actions.download")}
              </Button>
            </div>
          )
        }

        {message.quotedMsg.mediaType === "image" && message.quotedMsg.mediaUrl && (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
        )}
        {message.quotedMsg?.body && (
            <MarkdownWrapper>{message.quotedMsg.body}</MarkdownWrapper>
        )}


      </div>
    </div>
  );
};

const handleDrag = event => {
  event.preventDefault();
  event.stopPropagation();
  if (event.type === "dragenter" || event.type === "dragover") {
    setDragActive(true);
  } else if (event.type === "dragleave") {
    setDragActive(false);
  }
}

const isYouTubeLink = (url) => {
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return youtubeRegex.test(url);
};

const handleDrop = event => {
  event.preventDefault();
  event.stopPropagation();
  setDragActive(false);
  if (event.dataTransfer.files && event.dataTransfer.files[0]) {
    if (onDrop) {
      onDrop(event.dataTransfer.files);
    }
  }
}
const xmlRegex = /<([^>]+)>/g;
const boldRegex = /\*(.*?)\*/g;

const formatXml = (xmlString) => {
  // Verifica se o XML contém a assinatura com nome do atendente
  if (boldRegex.test(xmlString)) {
    // Formata o texto dentro da assinatura em negrito
    xmlString = xmlString.replace(boldRegex, "**$1**");
  }
  return xmlString;
};

const renderMessages = () => {
  if (messagesList.length > 0) {
    const viewMessagesList = messagesList.map((message, index) => {
      if (message.mediaType === "reactionMessage") {
        return null;
      }

      if (message.mediaType === "call_log") {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div className={classes.messageCenter}>
              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
              {isGroup && (
                <span className={classes.messageContactName} style={{ color: getContactColor(message.contact?.name) }}>
                  {message.contact?.name}
                </span>
              )}

              {/* {isGroup && (
                  <span className={classes.messageContactName}>
                    {JSON.parse(message.dataJson).pushName} #{message.contact?.name}
                  </span>
                )} */}
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                  <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={clsx(classes.messageLeftWrapper, {
                [classes.messageWrapperWithReaction]: getMessageReactionDetails(message).length > 0,
              })}
              onTouchStart={() => handleTouchStart(message)}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
            >
            <div
              className={classes.messageLeft}
              title={message.queueId && message.queue?.name}
              onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              style={((message.mediaType === "image" || message.mediaType === "video") && message.mediaUrl) ? { maxWidth: 330 } : undefined}
            >
              {renderMessageActionsButton(message)}
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  message={message}
                />
              )}

              {message.isForwarded && (
                <div>
                  <span className={classes.forwardMessage}
                  ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> {i18n.t("messagesResponsive.status.forwarded")}
                  </span>
                  <br />
                </div>
              )}
              {isGroup && (
                <span className={classes.messageContactName} style={{ color: getContactColor(message.contact?.name) }}>
                  {message.contact?.name}
                </span>
              )}
              {isYouTubeLink(message.body) && (
                <>
                  <YouTubePreview videoUrl={message.body} />
                </>
              )}

              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <span className={classes.deletedMessage}
                  >{i18n.t("messagesResponsive.status.deletedByContact")} &nbsp;
                  </span>
                </div>
              )}

               {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview" // Adicionado para aceitar o componente de preview de anúncio
                //|| message.mediaType === "multi_vcard"
              ) && checkMessageMedia(message)}

              {message.mediaType === "adMetaPreview" ? (
                <span className={classes.timestamp}>
                  {message.isEdited ? i18n.t("messagesResponsive.status.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                </span>
              ) : (
              <div className={clsx(classes.textContentItem, {
                [classes.textContentItemDeleted]: message.isDeleted,
              })}
                style={((message.mediaType === "image" || message.mediaType === "video") && message.mediaUrl && getBasename(message.mediaUrl).trim() === (message.body || "").trim()) ? { paddingBottom: 16 } : undefined}
              >
                {message.quotedMsg && renderQuotedMessage(message)}
                {
    (
      (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && getBasename(message.mediaUrl).trim() !== message.body.trim()) ||
      message.mediaType !== "audio" &&
      message.mediaType !== "image" &&
      message.mediaType !== "video" &&
      message.mediaType != "reactionMessage" &&
      message.mediaType != "locationMessage" &&
      message.mediaType !== "contactMessage"
    ) && (
      <>
        {xmlRegex.test(message.body) && (
          <span>{getMessageBody(message)}</span>
        )}
        {!xmlRegex.test(message.body) && (
          <MarkdownWrapper>{(lgpdDeleteMessage && message.isDeleted) ? i18n.t("messagesResponsive.status.deletedMessage") : getMessageBody(message)}</MarkdownWrapper>
        )}
        {renderTranslationIndicator(message)}
      </>
    )
}


                {message.quotedMsg && message.mediaType === "reactionMessage" && (
                  <>
                    <span style={{ marginLeft: "0px" }}>
                      <MarkdownWrapper>
                        {"" + message?.contact?.name + i18n.t("messagesResponsive.reactions.contactReacted") + message.body}
                      </MarkdownWrapper>
                    </span>
                  </>
                )}

                <span className={classes.timestamp}>
                  {message.isEdited ? i18n.t("messagesResponsive.status.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
              )}
            </div>
            {renderMessageReactions(message, false)}
            </div>
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={clsx(classes.messageRightWrapper, {
                [classes.messageRightWrapperWithReaction]: getMessageReactionDetails(message).length > 0,
              })}
              onTouchStart={() => handleTouchStart(message)}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
            >
            <div
              className={message.isPrivate ? classes.messageRightPrivate : classes.messageRight}
              title={message.queueId && message.queue?.name}
              onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              style={((message.mediaType === "image" || message.mediaType === "video") && message.mediaUrl) ? { maxWidth: 330 } : undefined}
            >
              {renderMessageActionsButton(message)}
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  message={message}
                />
              )}
              {message.isForwarded && (
                <div>
                  <span className={classes.forwardMessage}
                  ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> {i18n.t("messagesResponsive.status.forwarded")}
                  </span>
                  <br />
                </div>
              )}
              {isYouTubeLink(message.body) && (
                <>
                  <YouTubePreview videoUrl={message.body} />
                </>
              )}
              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <span className={classes.deletedMessage}
                  >{i18n.t("messagesResponsive.status.deletedByMe")} &nbsp;
                  </span>
                </div>
              )}
              {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview" // Adicionado para aceitar o componente de preview de anúncio
                //|| message.mediaType === "multi_vcard"
              ) && checkMessageMedia(message)}
              {message.mediaType === "adMetaPreview" ? (
                <span className={classes.timestamp}>
                  {message.isEdited ? i18n.t("messagesResponsive.status.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  {renderMessageAck(message)}
                </span>
              ) : (
              <div
                className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}
                style={((message.mediaType === "image" || message.mediaType === "video") && message.mediaUrl && getBasename(message.mediaUrl).trim() === (message.body || "").trim()) ? { paddingBottom: 16 } : undefined}
              >

                {message.quotedMsg && renderQuotedMessage(message)}

                {
                  ((message.mediaType === "image" || message.mediaType === "video") && getBasename(message.mediaUrl) === message.body) ||
                  (message.mediaType !== "audio" && message.mediaType != "reactionMessage" && message.mediaType != "locationMessage" && message.mediaType !== "contactMessage") && (
                    <>
                      {xmlRegex.test(message.body) && (
                        <div>{formatXml(
                          message.translatedBody
                            ? (!message.fromMe
                              ? (showOriginal[message.id] ? message.body : message.translatedBody)
                              : (showOriginal[message.id] ? message.translatedBody : message.body))
                            : message.body
                        )}</div>

                      )}
                      {!xmlRegex.test(message.body) && (<MarkdownWrapper>{
                        message.translatedBody
                          ? (!message.fromMe
                            ? (showOriginal[message.id] ? message.body : message.translatedBody)
                            : (showOriginal[message.id] ? message.translatedBody : message.body))
                          : message.body
                      }</MarkdownWrapper>)}
                      {renderTranslationIndicator(message)}
                    </>
                  )}

                {message.quotedMsg && message.mediaType === "reactionMessage" && (
                  <>
                    <span style={{ marginLeft: "0px" }}>
                      <MarkdownWrapper>
                        {i18n.t("messagesResponsive.reactions.youReacted") + message.body}
                      </MarkdownWrapper>
                    </span>
                  </>
                )}

                <span className={classes.timestamp}>
                  {message.isEdited ? i18n.t("messagesResponsive.status.edited") + " " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  {renderMessageAck(message)}
                </span>
              </div>
              )}
            </div>
            {renderMessageReactions(message, true)}
            </div>
          </React.Fragment>
        );
      }
    });
    return viewMessagesList;
  } else {
    return <div>{i18n.t("messagesResponsive.placeholder.sayHello")}</div>;
  }
};

return (
  <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
    {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>{i18n.t("messagesResponsive.actions.dropFileHere")}</div>}

    {/* Popover de confirmação de exclusão */}
    {Boolean(deleteAnchorEl) && (
      <Popper open={true} anchorEl={deleteAnchorEl} placement="top" style={{ zIndex: 9999 }}>
        <ClickAwayListener onClickAway={handleCancelDelete}>
          <Paper style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            fontSize: 13,
            fontWeight: 500,
          }}>
            <span>{i18n.t("messageOptionsMenu.confirmationModal.message")}</span>
            <span
              onClick={handleConfirmDelete}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                color: "#ffffff",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)"; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M5 14L8.5 17.5L19 6.5" />
              </svg>
            </span>
            <span
              onClick={handleCancelDelete}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                color: "#888",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)"; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </span>
          </Paper>
        </ClickAwayListener>
      </Popper>
    )}
    <EditMessageModal
      open={editModalOpen}
      onClose={() => setEditModalOpen(false)}
      message={editTargetMsg}
      onMessageUpdated={handleMessageUpdated}
    />

    {/* Popover de reações inline */}
    {Boolean(reactAnchorEl) && (
      <Popper open={true} anchorEl={reactAnchorEl} placement="top" style={{ zIndex: 9999 }}>
        <ClickAwayListener onClickAway={() => { setReactAnchorEl(null); setReactTargetMsg(null); }}>
          <Paper style={{ display: "flex", gap: 2, padding: "6px 8px", borderRadius: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}>
            {availableReactionsInline.map(r => (
              <span
                key={r}
                style={{ cursor: "pointer", fontSize: 20, padding: "2px 3px", borderRadius: 8, transition: "background 0.15s" }}
                onClick={() => handleReactToMessage(r)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.06)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {r}
              </span>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>
    )}

    <MessageOptionsMenu
      message={selectedMessage}
      anchorEl={anchorEl}
      menuOpen={messageOptionsMenuOpen}
      handleClose={handleCloseMessageOptionsMenu}
      isGroup={isGroup}
      whatsappId={whatsappId}
      queueId={queueId}
      contactId={contactId}
      onMessageUpdated={handleMessageUpdated}
      onReactionAdded={handleReactionAdded}
    />
    <div
      id="messagesList"
      className={classes.messagesList}
      style={bottomPadding ? { paddingBottom: bottomPadding + 10 } : undefined}
      onScroll={handleScroll}
      onMouseUp={handleTextSelection}
    >
      {messagesList.length > 0 ?
        renderMessages()
        : []}
    </div>

    {Boolean(selectionAnchorEl) && selectionText && (
      <Popper
        open={true}
        anchorEl={selectionAnchorEl}
        placement="top"
        style={{ zIndex: 9999 }}
      >
        <ClickAwayListener onClickAway={() => { setSelectionAnchorEl(null); setSelectionText(""); }}>
          <Paper
            className={classes.saveFieldFab}
            onClick={() => { setSaveFieldOpen(true); setSelectionAnchorEl(null); }}
          >
            <SaveAltIcon size={16} />
            Salvar no contato
          </Paper>
        </ClickAwayListener>
      </Popper>
    )}

    <SaveFieldFromTextMenu
      open={saveFieldOpen}
      onClose={() => { setSaveFieldOpen(false); setSelectionText(""); }}
      selectedText={selectionText}
      contactId={contactId}
    />

    {(channel !== "whatsapp" && channel !== undefined) && (
      <div
        style={{
          width: "100%",
          display: "flex",
          padding: "10px",
          alignItems: "center",
          backgroundColor: "#E1F3FB",
        }}
      >
        {channel === "facebook" ? (
          <Facebook />
        ) : (
          <Instagram />
        )}

        <span>
          {i18n.t("messagesResponsive.warnings.facebookPolicy")}
        </span>
      </div>
    )}

    <Dialog
      open={reactionDialog.open}
      onClose={() => {
        setReactionDialog({ open: false, reactions: [] });
        setReactionDialogEmojiAnchor(null);
      }}
      PaperProps={{ className: classes.reactionsDialogPaper }}
    >
      <div className={classes.reactionsDialogContent}>
        <div className={classes.reactionsHeader}>
          <div className={classes.reactionsTitle}>
            {reactionDialog.reactions.length} {reactionDialog.reactions.length === 1 ? "reação" : "reações"}
          </div>
          <div className={classes.reactionsTabs}>
            <button
              type="button"
              className={classes.reactionsAllTab}
              onClick={(event) => setReactionDialogEmojiAnchor(event.currentTarget)}
              aria-label="Adicionar reação"
            >
              <SmilePlus size={20} strokeWidth={1.8} />
            </button>
            {[...new Set(reactionDialog.reactions.map((item) => item.reaction))].map((reaction) => {
              const count = reactionDialog.reactions.filter((item) => item.reaction === reaction).length;
              return (
                <span key={reaction} className={classes.reactionsEmojiTab}>
                  <span>{reaction}</span>
                  <span className={classes.reactionCount}>{count}</span>
                </span>
              );
            })}
          </div>
        </div>
        <div className={classes.reactionsList}>
          {reactionDialog.reactions.map((item) => {
            const initial = (item.name || "?").trim().charAt(0).toUpperCase();
            return (
              <div
                key={item.key}
                className={clsx(classes.reactionDialogRow, {
                  [classes.reactionDialogRowSelf]: item.isSelf,
                })}
              >
                <span className={classes.reactionAvatar}>{initial}</span>
                <span className={classes.reactionUserInfo}>
                  <div className={classes.reactionUserName}>{item.name}</div>
                  {item.isSelf && <div className={classes.reactionHint}>Sua reação</div>}
                </span>
                <span className={classes.reactionDialogEmoji}>{item.reaction}</span>
                {item.isSelf && (
                  <button
                    type="button"
                    className={classes.reactionRemoveButton}
                    onClick={() => handleRemoveOwnReaction(item)}
                    aria-label="Excluir reação"
                  >
                    <Trash2 size={16} strokeWidth={2} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>

    <Popper
      open={Boolean(reactionDialogEmojiAnchor)}
      anchorEl={reactionDialogEmojiAnchor}
      placement="bottom-start"
      style={{ zIndex: 10000 }}
    >
      <ClickAwayListener onClickAway={() => setReactionDialogEmojiAnchor(null)}>
        <div className={classes.reactionEmojiPicker}>
          <Picker
            perLine={16}
            theme={mobileTheme.mode === "light" ? "light" : "dark"}
            i18n={{
              search: i18n.t("emoji.search") || "Pesquisar",
              notfound: i18n.t("emoji.notfound") || "Nenhum emoji encontrado",
              categories: {
                search: i18n.t("emoji.categories.search") || "Resultados",
                recent: i18n.t("emoji.categories.recent") || "Recentes",
                smileys: i18n.t("emoji.categories.smileys") || "Rostos e Emoções",
                people: i18n.t("emoji.categories.people") || "Pessoas",
                nature: i18n.t("emoji.categories.nature") || "Animais e Natureza",
                foods: i18n.t("emoji.categories.foods") || "Comidas e Bebidas",
                activity: i18n.t("emoji.categories.activity") || "Atividades",
                places: i18n.t("emoji.categories.places") || "Viagens e Lugares",
                objects: i18n.t("emoji.categories.objects") || "Objetos",
                symbols: i18n.t("emoji.categories.symbols") || "Símbolos",
                flags: i18n.t("emoji.categories.flags") || "Bandeiras",
                custom: i18n.t("emoji.categories.custom") || "Personalizados",
              },
            }}
            showPreview={true}
            showSkinTones={false}
            onSelect={(emoji) => handleDialogReactionSelect(emoji.native)}
          />
        </div>
      </ClickAwayListener>
    </Popper>

    {loading && (
      <div>
        <CircularProgress className={classes.circleLoading} />
      </div>
    )}

    {/* Bottom sheet mobile — ações da mensagem */}
    {isMobile && (
      <Dialog
        open={bottomSheetOpen}
        onClose={closeBottomSheet}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        PaperProps={{ className: classes.bottomSheet, style: { position: "fixed", bottom: 0, margin: 0, width: "100%", maxWidth: "100%" } }}
        style={{ zIndex: 9999 }}
      >
        <div className={classes.bottomSheetHandle} />
        <List disablePadding>
          {/* Responder */}
          <ListItem button className={classes.bottomSheetItem} onClick={() => { closeBottomSheet(); if (longPressMsg) { hanldeReplyMessage({}, longPressMsg); focusMessageInput(); } }}>
            <ListItemIcon className={classes.bottomSheetIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 5.5L4.5 10.5L9.5 15.5" /><path d="M4.5 10.5H15.5C17.7091 10.5 19.5 12.2909 19.5 14.5V18.5" /></svg>
            </ListItemIcon>
            <ListItemText primary={i18n.t("messageOptionsMenu.reply")} />
          </ListItem>

          {/* Encaminhar */}
          <ListItem button className={classes.bottomSheetItem} onClick={() => { closeBottomSheet(); if (longPressMsg) handleInlineForward(longPressMsg); }}>
            <ListItemIcon className={classes.bottomSheetIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6.65032C9 6.65032 15.9383 6.10759 16.9154 7.08463C17.8924 8.06167 17.3496 15 17.3496 15M16.5 7.5L6.5 17.5" /></svg>
            </ListItemIcon>
            <ListItemText primary={i18n.t("messageOptionsMenu.forward")} />
          </ListItem>

          {/* Copiar */}
          {longPressMsg && longPressMsg.body && (
            <ListItem button className={classes.bottomSheetItem} onClick={() => { closeBottomSheet(); navigator.clipboard.writeText(longPressMsg.body); }}>
              <ListItemIcon className={classes.bottomSheetIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 15C9 12.1716 9 10.7574 9.87868 9.87868C10.7574 9 12.1716 9 15 9H16C18.8284 9 20.2426 9 21.1213 9.87868C22 10.7574 22 12.1716 22 15V16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H15C12.1716 22 10.7574 22 9.87868 21.1213C9 20.2426 9 18.8284 9 16V15Z" /><path d="M16.9999 9C16.9975 6.04291 16.9528 4.51121 16.092 3.46243C15.9258 3.25989 15.7401 3.07418 15.5376 2.90796C14.4312 2 12.7875 2 9.5 2C6.21252 2 4.56878 2 3.46243 2.90796C3.25989 3.07417 3.07418 3.25989 2.90796 3.46243C2 4.56878 2 6.21252 2 9.5C2 12.7875 2 14.4312 2.90796 15.5376C3.07417 15.7401 3.25989 15.9258 3.46243 16.092C4.51121 16.9528 6.04291 16.9975 9 16.9999" /></svg>
              </ListItemIcon>
              <ListItemText primary={i18n.t("messageOptionsMenu.copy")} />
            </ListItem>
          )}

          {/* Favoritar */}
          <ListItem button className={classes.bottomSheetItem} onClick={() => { closeBottomSheet(); if (longPressMsg) handleInlineStar(longPressMsg); }}>
            <ListItemIcon className={classes.bottomSheetIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill={longPressMsg?.isStarred ? "#f9a825" : "none"} stroke={longPressMsg?.isStarred ? "#f9a825" : "currentColor"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13.7276 3.44418L15.4874 6.99288C15.7274 7.48687 16.3673 7.9607 16.9073 8.05143L20.0969 8.58575C22.1367 8.92853 22.6167 10.4206 21.1468 11.8925L18.6671 14.3927C18.2471 14.8161 18.0172 15.6327 18.1471 16.2175L18.8571 19.3125C19.417 21.7623 18.1271 22.71 15.9774 21.4296L12.9877 19.6452C12.4478 19.3226 11.5579 19.3226 11.0079 19.6452L8.01827 21.4296C5.8785 22.71 4.57865 21.7522 5.13859 19.3125L5.84851 16.2175C5.97849 15.6327 5.74852 14.8161 5.32856 14.3927L2.84884 11.8925C1.389 10.4206 1.85895 8.92853 3.89872 8.58575L7.08837 8.05143C7.61831 7.9607 8.25824 7.48687 8.49821 6.99288L10.258 3.44418C11.2179 1.51861 12.7777 1.51861 13.7276 3.44418Z" /></svg>
            </ListItemIcon>
            <ListItemText primary={longPressMsg?.isStarred ? i18n.t("messageOptionsMenu.unfavorite") : i18n.t("messageOptionsMenu.favorite")} />
          </ListItem>

          {/* Reagir */}
          <ListItem button className={classes.bottomSheetItem} onClick={(e) => { closeBottomSheet(); if (longPressMsg) handleInlineReact(e, longPressMsg); }}>
            <ListItemIcon className={classes.bottomSheetIcon}>
              <SmilePlus size={22} strokeWidth={1.8} />
            </ListItemIcon>
            <ListItemText primary={i18n.t("messageOptionsMenu.react")} />
          </ListItem>

          {/* Editar */}
          {canEditMessage(longPressMsg) && (
            <ListItem button className={classes.bottomSheetItem} onClick={() => { closeBottomSheet(); if (longPressMsg) { handleInlineEdit(longPressMsg); focusMessageInput(); } }}>
              <ListItemIcon className={classes.bottomSheetIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15.2141 5.98239L16.6158 4.58063C17.39 3.80646 18.6452 3.80646 19.4194 4.58063C20.1935 5.3548 20.1935 6.60998 19.4194 7.38415L18.0176 8.78591M15.2141 5.98239L6.98023 14.2163C5.93493 15.2616 5.41226 15.7842 5.05637 16.4211C4.70047 17.058 4.3424 18.5619 4 20C5.43809 19.6576 6.94199 19.2995 7.57889 18.9436C8.21579 18.5877 8.73844 18.0651 9.78375 17.0198L18.0176 8.78591M15.2141 5.98239L18.0176 8.78591" /><path d="M11 20H17" /></svg>
              </ListItemIcon>
              <ListItemText primary={i18n.t("messageOptionsMenu.edit")} />
            </ListItem>
          )}

          {/* Apagar (só fromMe) */}
          {longPressMsg?.fromMe && !longPressMsg?.isDeleted && (
            <ListItem button className={classes.bottomSheetItem} onClick={(e) => { closeBottomSheet(); if (longPressMsg) handleInlineDelete(longPressMsg, e); }}>
              <ListItemIcon className={`${classes.bottomSheetIcon} ${classes.bottomSheetDanger}`}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5" /><path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" /><path d="M9.5 16.5L9.5 10.5" /><path d="M14.5 16.5L14.5 10.5" /></svg>
              </ListItemIcon>
              <ListItemText primary={i18n.t("messageOptionsMenu.erase")} style={{ color: "#e53935" }} />
            </ListItem>
          )}
        </List>
      </Dialog>
    )}
  </div>
);
};

export default MessagesList;
